import { Component, Mixins, Prop } from "vue-property-decorator";
import RequestPromises from "@/mixins/requestPromises";
import { DataModules } from "@/store/modules/data";
import store from "@/store";
import { ITemplate, ITemplateContent } from "@/models/templates";
import { TemplateTypes } from "@/data/enums/templates";

@Component
export default class TemplateMixin extends Mixins(RequestPromises) {
  // Props

  @Prop()
  public scope!: string;

  @Prop({ required: true })
  public templateId!: ITemplate["id"];

  @Prop({ default: null })
  public templateCode!: ITemplate["code"];

  @Prop({ default: null })
  public templateType!: TemplateTypes;

  // Data

  public hasRequestError = false;
  public isLoading = true;
  public isReloading = false;

  // Life-cycle Methods
  public beforeDestroy() {
    store.dispatch("data/binSingle", {
      scope: this.templateScope,
      storeModule: DataModules.TEMPLATES,
      vm: this
    });
  }

  // Computed

  public get isNew() {
    return !this.templateId;
  }

  public get computedTemplateType() {
    return this.templateType || this.template?.type;
  }

  public get isDefault() {
    return this.template?.default;
  }

  public get canSave() {
    return this.isNew ? this.canCreate : this.canEdit;
  }

  public get isNotificationTemplate() {
    return this.computedTemplateType === TemplateTypes.NOTIFICATION;
  }

  public get isClientAreaTemplate() {
    return this.computedTemplateType === TemplateTypes.CLIENT_TEMPLATE;
  }

  public get templateScope() {
    return (
      this.scope ||
      store.getters[`data/${DataModules.TEMPLATES}/scope`](
        this.templateId || this.templateCode
      )
    );
  }

  public get template() {
    return store.getters["data/single"]({
      id: this.templateId,
      scope: this.templateScope,
      storeModule: DataModules.TEMPLATES
    }) as ITemplate;
  }

  public get withParam() {
    return store.getters[`data/${DataModules.TEMPLATES}/withParam`]({
      type: this.templateType
    });
  }

  public get canCreate() {
    return store.getters[`data/${DataModules.TEMPLATES}/canCreate`]();
  }

  public get canEdit() {
    return store.getters[`data/${DataModules.TEMPLATES}/canEdit`]();
  }

  public get canDelete() {
    return store.getters[`data/${DataModules.TEMPLATES}/canDelete`]();
  }

  public get canDuplicate() {
    return store.getters[`data/${DataModules.TEMPLATES}/canDuplicate`]();
  }

  // Methods

  public async loadData({ ignoreStored } = { ignoreStored: false }) {
    try {
      return this.getTemplate({
        ignoreStored
      });
    } catch (error) {
      store.dispatch("api/handleError", error);
      this.hasRequestError = true;
    } finally {
      this.isLoading = false;
      this.isReloading = false;
    }
  }

  public async getTemplate(
    config: any = { ignoreStored: false }
  ): Promise<any> {
    if (!this.templateId && !this.templateCode) return Promise.resolve();
    return store.dispatch(`data/${DataModules.TEMPLATES}/get`, {
      id: this.templateId || this.templateCode,
      scope: this.templateScope,
      ...(this.withParam
        ? {
            params: {
              with: this.withParam
            }
          }
        : {}),
      ...config,
      vm: this
    });
  }

  public async reloadContents() {
    /**
     * Reload contents after translation has been updated
     */
    const template = await this.getTemplate({
      ignoreStored: true,
      storeData: false
    });

    store.commit(`data/replaceByPath`, {
      storeModule: DataModules.TEMPLATES,
      scope: this.templateScope,
      path: `data.contents`,
      data: template.contents
    });
  }

  public reloadData() {
    this.isReloading = true;
    return this.loadData({ ignoreStored: true });
  }

  public async createContents(
    templateId: ITemplate["id"],
    data: Partial<ITemplateContent>
  ) {
    return this.$store.dispatch(
      `data/${DataModules.TEMPLATES_CONTENTS}/create`,
      {
        templateId,
        data
      }
    );
  }

  public async updateContents(
    templateContentId: ITemplateContent["id"],
    templateId: ITemplate["id"],
    data: Partial<ITemplateContent>
  ) {
    return this.$store.dispatch(
      `data/${DataModules.TEMPLATES_CONTENTS}/update`,
      {
        id: templateContentId,
        templateId,
        data
      }
    );
  }

  public async createTemplate(data: Partial<ITemplate>) {
    return this.$store.dispatch(`data/${DataModules.TEMPLATES}/create`, {
      type: this.templateType,
      data
    });
  }

  public duplicate(id: ITemplate["id"]) {
    return this.$store.dispatch(`data/${DataModules.TEMPLATES}/duplicate`, {
      id
    }) as Promise<ITemplate>;
  }

  public async updateTemplate(data: Partial<ITemplate>, templateId = null) {
    let replica: ITemplate | null = null;
    if (this.isDefault || !!templateId) {
      replica = await this.duplicate(templateId || this.templateId);
    }

    return this.$store.dispatch(`data/${DataModules.TEMPLATES}/update`, {
      id: replica?.id || this.template?.id || this.templateId,
      data: this.$_.omit(data, ["brand_id"])
    });
  }

  public deleteTemplate() {
    return this.$store.dispatch(`data/${DataModules.TEMPLATES}/remove`, {
      id: this.template?.id || this.templateId
    });
  }
}
